import { AlertTriangle, Info, Loader2, Lock, RefreshCw, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { ApiError } from "@hostdock/sdk";
import { Button } from "./button";
import { cn } from "./cn";

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("hd-skeleton rounded bg-surface-3", className)} aria-hidden />;
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 text-sm text-muted">
      <Loader2 size={15} className="animate-spin" /> {label}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }: { icon?: LucideIcon; title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      {Icon && (
        <div className="relative mb-2">
          <div className="hd-gradient-soft absolute -inset-3 rounded-3xl" aria-hidden />
          <div className="hd-gradient relative flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-accent">
            <Icon size={21} strokeWidth={1.9} />
          </div>
        </div>
      )}
      <p className="text-[15px] font-semibold">{title}</p>
      {description && <p className="max-w-sm text-[13px] text-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function describeError(error: unknown): { title: string; message: string; requestId: string | null } {
  if (error instanceof ApiError) {
    if (error.status === 0) return { title: "Connection problem", message: error.message, requestId: null };
    if (error.status >= 500) return { title: "Something went wrong", message: error.message, requestId: error.requestId };
    return { title: "Request failed", message: error.message, requestId: error.requestId };
  }
  return { title: "Something went wrong", message: error instanceof Error ? error.message : "Unknown error", requestId: null };
}

export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  if (error instanceof ApiError && error.isForbidden) return <PermissionDenied className={className} />;
  const d = describeError(error);
  return (
    <div role="alert" className={cn("flex flex-col items-center gap-2 px-6 py-12 text-center", className)}>
      <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg bg-danger-soft text-danger">
        <AlertTriangle size={18} />
      </div>
      <p className="text-sm font-semibold">{d.title}</p>
      <p className="max-w-md text-[13px] text-muted">{d.message}</p>
      {d.requestId && <p className="font-mono text-[11.5px] text-subtle">Request ID: {d.requestId}</p>}
      {onRetry && (
        <Button size="sm" className="mt-2" icon={<RefreshCw size={13} />} onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function PermissionDenied({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-col items-center gap-2 px-6 py-12 text-center", className)}>
      <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-surface-2 text-muted">
        <Lock size={18} />
      </div>
      <p className="text-sm font-semibold">You don't have access to this</p>
      <p className="max-w-sm text-[13px] text-muted">Ask an administrator with security access to grant the permission you need.</p>
    </div>
  );
}

type Tone = "info" | "warning" | "danger" | "success";
const TONES: Record<Tone, string> = {
  info: "border-info/25 bg-info-soft text-fg",
  warning: "border-warning/30 bg-warning-soft text-fg",
  danger: "border-danger/30 bg-danger-soft text-fg",
  success: "border-success/30 bg-success-soft text-fg",
};
const TONE_ICON: Record<Tone, string> = { info: "text-info", warning: "text-warning", danger: "text-danger", success: "text-success" };

export function Notice({ tone = "info", title, children, action }: { tone?: Tone; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const Icon = tone === "info" || tone === "success" ? Info : AlertTriangle;
  return (
    <div className={cn("flex flex-wrap items-start gap-x-3 gap-y-2.5 rounded-xl border px-4 py-3 text-[13px]", TONES[tone])} role={tone === "danger" ? "alert" : "status"}>
      <Icon size={16} className={cn("mt-0.5 shrink-0", TONE_ICON[tone])} />
      <div className="min-w-0 flex-[1_1_12rem] break-words">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="text-muted">{children}</div>}
      </div>
      {action && <div className="shrink-0 pl-7 sm:pl-0">{action}</div>}
    </div>
  );
}

/** Inline form-level error for a failed mutation. */
export function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  const d = describeError(error);
  return (
    <Notice tone="danger" title={d.message}>
      {d.requestId ? <span className="font-mono text-[11.5px]">Request ID: {d.requestId}</span> : null}
    </Notice>
  );
}
